import React from 'react'
import { useTranslation } from 'react-i18next'
import { MapContainer, TileLayer } from 'react-leaflet'

import 'leaflet.sync'

import { makeStyles } from '@material-ui/core/styles'
import Box from '@material-ui/core/Box'
import CircularProgress from '@material-ui/core/CircularProgress'

import LayerLegend from '../../comparator/LayerLegend'
import { getParcelBounds } from '../../../../../utils/map'
import { useParcelZoningKmean } from '../../../../../adapters/sentinel'
import LeafletLayersControl from '../../../LeafletLayersControl'
import { usePaletteScript } from '../../../../../adapters/paletteRange'
import { usePaletteAdjustmentsEnabled } from '../../../../../context/PaletteAdjustmentsContext'
import { rescaleLegend } from '../../../../../utils/palette'

import ZoneLayer from '../ZoneLayer'

// Center map on Granada
const position = [37.741934, -3.188515]

const useStyles = makeStyles((theme) => ({
  root: { flexGrow: 1 },
  loading: {
    marginBottom: theme.spacing(20),
    marginTop: theme.spacing(20),
  },
}))

export default function FertilizerMapKmean({
  parcel,
  imagesDate,
  map,
  setMap,
  layer,
  zones,
  setZonedGeom,
  maxCC,
}) {
  const classes = useStyles()
  const { t } = useTranslation()

  const [activeBaseLayer, setActiveBaseLayer] = React.useState('Maps Hybrid')
  const [overlays, setOverlays] = React.useState({
    'Zoned NDVI': true,
    'Zoned transparent NDVI': true,
  })

  // Zones are coloured from the legend: follow the user's saved palette range for this
  // farm/layer (only in apps that enable it). Only the range is needed here, no image.
  const paletteEnabled = usePaletteAdjustmentsEnabled()
  const { data: palette } = usePaletteScript(
    { parcelId: parcel.id, layerKey: layer.key, rangesOnly: true },
    { enabled: paletteEnabled }
  )
  const hasCustomRange = palette?.min != null && palette?.max != null
  const legendLayer = React.useMemo(
    () =>
      hasCustomRange
        ? { ...layer, legend: rescaleLegend(layer.legend, palette) }
        : layer,
    [layer, hasCustomRange, palette?.min, palette?.max]
  )

  const { isLoading, data, refetch } = useParcelZoningKmean({
    parcel_id: parcel.id,
    layer: layer.name,
    images_date: imagesDate,
    zones,
    maxcc: maxCC,
  })

  React.useEffect(() => {
    if (imagesDate.length > 0) refetch()
  }, [imagesDate, zones])

  React.useEffect(() => {
    if (data) setZonedGeom({ type: 'seed', value: data })
  }, [data])

  React.useEffect(() => {
    if (map) {
      try {
        map.fitBounds(getParcelBounds(parcel))
      } catch (error) {
        console.error(error)
      }
    }
  }, [map])

  const baseLayers = [{ name: 'Maps Hybrid', checked: activeBaseLayer === 'Maps Hybrid' }]
  const overlayGroups = [
    {
      name: 'Zonification',
      overlays: [
        { name: 'Zoned NDVI', visible: overlays['Zoned NDVI'] },
        { name: 'Zoned transparent NDVI', visible: overlays['Zoned transparent NDVI'] },
      ],
    },
  ]

  const handleOverlayToggle = (name, visible) =>
    setOverlays((prev) => ({ ...prev, [name]: visible }))

  return (
    <>
      {data ? (
        <MapContainer
          center={position}
          minZoom={2}
          maxZoom={24}
          zoom={9}
          style={{ width: '100%', height: 400, borderRadius: 4 }}
          zoomControl={false}
          whenCreated={setMap}
        >
          <TileLayer
            attribution="Maps Hybrid"
            url={`https://mt1.google.com/vt/lyrs=y&x={x}&y={y}&z={z}`}
          />

          {overlays['Zoned NDVI'] && (
            <ZoneLayer
              key={`zones-1-${hasCustomRange ? `${palette.min}-${palette.max}` : 'default'}`}
              data={data}
              color="red"
              name="Zoned Parcel"
              legend={legendLayer.legend}
              opacity={1}
            />
          )}
          {overlays['Zoned transparent NDVI'] && (
            <ZoneLayer
              key={`zones-0-${hasCustomRange ? `${palette.min}-${palette.max}` : 'default'}`}
              data={data}
              color="red"
              name="Zoned Parcel"
              legend={legendLayer.legend}
              opacity={0}
            />
          )}

          <LeafletLayersControl
            baseLayers={baseLayers}
            overlayGroups={overlayGroups}
            onBaseLayerChange={setActiveBaseLayer}
            onOverlayToggle={handleOverlayToggle}
            top={12}
            right={12}
          />

          <LayerLegend layer={legendLayer} customRange={hasCustomRange} />
        </MapContainer>
      ) : (
        <Box display="flex" justifyContent="center">
          <CircularProgress className={classes.loading} />
        </Box>
      )}
    </>
  )
}
